import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { format } from "date-fns";
import { Loader2, ShieldCheck, Coins, ArrowRightLeft, Search, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const KIND_LABEL = {
  all: "All events",
  impersonation: "Impersonations",
  credits: "Credit overrides",
};

/**
 * Admin Audit Timeline — single screen that consolidates impersonation events
 * and admin-driven credit overrides for compliance review.
 *
 * Backed by `GET /api/admin/audit/timeline` (admin-only).
 */
export default function AuditTimelinePage() {
  const [kind, setKind] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = async (k = kind) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/audit/timeline`, {
        params: k === "all" ? { limit: 300 } : { limit: 300, kind: k },
        withCredentials: true,
      });
      setRows(r.data?.rows || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(kind); /* eslint-disable-line */ }, [kind]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r =>
      [r.actor_email, r.target_email, r.summary, r.note]
        .filter(Boolean)
        .some(s => String(s).toLowerCase().includes(q))
    );
  }, [rows, search]);

  return (
    <div className="page-container max-w-6xl mx-auto px-4 sm:px-8 py-8" data-testid="audit-timeline-page">
      <header className="mb-6">
        <p className="text-xs text-gray-400">Admin / Audit</p>
        <h1 className="text-2xl font-bold text-gray-900 font-['Manrope'] flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-teal-700" />
          Audit Timeline
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          One-click compliance review — every impersonation event and every admin-driven credit override.
        </p>
      </header>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
        <div className="inline-flex bg-gray-100 rounded-lg p-1" data-testid="audit-kind-toggle">
          {Object.keys(KIND_LABEL).map(k => (
            <button
              key={k}
              onClick={() => setKind(k)}
              data-testid={`audit-kind-${k}`}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                kind === k ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-900"
              }`}
            >
              {KIND_LABEL[k]}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search admin or target email..."
            className="pl-9"
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid="audit-search"
          />
        </div>
        <Button variant="outline" size="sm" onClick={() => load(kind)} data-testid="audit-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Refresh
        </Button>
      </div>

      {/* Timeline */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-gray-400 py-12 text-center" data-testid="audit-empty">
          No audit events {kind === "all" ? "yet" : `in this category`}.
        </p>
      ) : (
        <ol className="relative border-l border-gray-200 ml-3 space-y-3" data-testid="audit-timeline">
          {filtered.map((r, i) => (
            <AuditRow key={r.id || `${r.kind}-${r.date || i}-${r.target_user_id || i}`} row={r} idx={i} />
          ))}
        </ol>
      )}
    </div>
  );
}

function AuditRow({ row, idx }) {
  const isImp = row.kind === "impersonation";
  const isCredit = row.kind === "credits";
  const Icon = isImp ? ArrowRightLeft : Coins;

  // Color tokens by event type
  const dotClass = isImp
    ? "bg-amber-100 text-amber-700 ring-amber-50"
    : (row.delta || 0) > 0
      ? "bg-emerald-100 text-emerald-700 ring-emerald-50"
      : "bg-red-100 text-red-700 ring-red-50";

  return (
    <li className="ml-6" data-testid={`audit-row-${idx}`}>
      <span className={`absolute -left-3.5 flex w-7 h-7 items-center justify-center rounded-full ring-4 ${dotClass}`}>
        <Icon className="w-3.5 h-3.5" />
      </span>
      <div className="bg-white border border-gray-200 rounded-lg p-3.5">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <p className="text-sm font-semibold text-gray-900">{row.summary}</p>
          <time className="text-xs text-gray-400 whitespace-nowrap">
            {row.date ? format(new Date(row.date), "MMM d, yyyy · HH:mm") : "—"}
          </time>
        </div>
        <div className="mt-1.5 flex items-center gap-x-4 gap-y-0.5 flex-wrap text-xs text-gray-500">
          <span>
            <span className="text-gray-400">Admin:</span>{" "}
            <strong className="text-gray-700 font-medium">{row.actor_email || "—"}</strong>
          </span>
          {row.target_email && (
            <span>
              <span className="text-gray-400">Target:</span>{" "}
              <strong className="text-gray-700 font-medium">{row.target_email}</strong>
            </span>
          )}
          {isCredit && row.balance_after != null && (
            <span>
              <span className="text-gray-400">Balance after:</span>{" "}
              <strong className="text-gray-700 font-medium">{row.balance_after}</strong>
            </span>
          )}
          {isCredit && row.note && row.note !== row.action && (
            <span className="italic text-gray-500">"{row.note}"</span>
          )}
          {isImp && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 text-[10px] font-semibold uppercase tracking-wide">
              {row.action}
            </span>
          )}
        </div>
      </div>
    </li>
  );
}
